import { useEffect, useState } from "react";
import { TBusyTime, TMyClass } from "../@types";
import { apis, callApi } from "../api";
import moment from "moment";
import { eventNames, logEvent } from "../utils/logEvent";
import { toast } from "react-toastify";

export type TScheduleService = {
	loading: boolean;
	bookings: TMyClass[];
	busyTimes: TBusyTime[];
	listeners: (() => void)[];

	addEventListener: (cb: () => void) => void;
	clearData: () => void;
	emitChanges: () => void;
	removeEventListener: (cb: () => void) => void;

	setLoading: (loading: boolean) => void;
	setBookings: (bookings: TMyClass[]) => void;
	setBusyTimes: (busyTimes: TBusyTime[]) => void;
	addBusyTimes: (busyTimes: TBusyTime[]) => void;
	deleteBusyTime: (id: number) => void;
	reschedule: (id: number, booking: TMyClass) => void;
};

export const ScheduleService: TScheduleService = {
	loading: false,
	bookings: [],
	busyTimes: [],
	listeners: [],

	addEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
		this.listeners.push(cb);
	},

	clearData() {
		this.loading = false;
		this.bookings = [];
		this.busyTimes = [];
		this.emitChanges();
	},

	emitChanges() {
		for (const listener of this.listeners) {
			listener();
		}
	},

	removeEventListener(cb) {
		this.listeners = this.listeners.filter((listener) => listener !== cb);
	},

	setLoading(loading) {
		this.loading = loading;
	},

	setBookings(bookings) {
		this.bookings = bookings;
	},

	setBusyTimes(busyTimes) {
		this.busyTimes = busyTimes;
	},

	addBusyTimes(busyTimes) {
		this.busyTimes = [...this.busyTimes, ...busyTimes];
	},

	deleteBusyTime(id) {
		this.busyTimes = this.busyTimes.filter((busyTime) => busyTime.id !== id);
	},

	reschedule(id, booking) {
		const index = this.bookings.findIndex((b) => b.id === id);

		if (index === -1) {
			return;
		}

		this.bookings.splice(index, 1, booking);
		this.bookings = [...this.bookings];
	},
};

export function useScheduleInit() {
	useEffect(() => {
		const getMyBooking = async () => {
			try {
				ScheduleService.setLoading(true);
				ScheduleService.emitChanges();

				const response = await callApi({
					method: "GET",
					url: apis.GET_BOOKING,
				});

				if (!response) {
					return;
				}

				if (response.bookings) {
					ScheduleService.setBookings(response.bookings);
				}
				if (response.upcomingSchedules) {
					ScheduleService.setBusyTimes(response.upcomingSchedules);
				}
			} catch (error) {
			} finally {
				ScheduleService.setLoading(false);
				ScheduleService.emitChanges();
			}
		};

		getMyBooking();
	}, []);
}

export function useSchedule() {
	const [loading, setLoading] = useState(ScheduleService.loading);
	const [bookings, setBookings] = useState(ScheduleService.bookings);
	const [busyTimes, setBusyTimes] = useState(ScheduleService.busyTimes);

	useEffect(() => {
		const updateData = () => {
			setLoading(ScheduleService.loading);
			setBookings(ScheduleService.bookings);
			setBusyTimes(ScheduleService.busyTimes);
		};

		ScheduleService.addEventListener(updateData);

		return () => {
			ScheduleService.removeEventListener(updateData);
		};
	}, []);

	const addBusyTime = async (body: {
		startDate: string;
		endDate: string;
		blockTime: number;
	}) => {
		try {
			const response = await callApi({
				method: "POST",
				url: apis.CLASSES_SCHEDULE,
				body,
			});

			if (!response) {
				return;
			}

			ScheduleService.addBusyTimes(response.schedules);
			ScheduleService.emitChanges();
		} catch (error) {}
	};

	const deleteBusyTime = async (id: number) => {
		try {
			const response = await callApi({
				method: "DELETE",
				url: `${apis.CLASSES_SCHEDULE}/${id}`,
			});

			if (!response) {
				return;
			}

			ScheduleService.deleteBusyTime(id);
			ScheduleService.emitChanges();
		} catch (error) {}
	};

	const reschedule = async (
		bookingId: number,
		body: { startDate: string; lessonId: number },
		newBooking: TMyClass,
	) => {
		try {
			const response = await callApi({
				method: "PATCH",
				url: `${apis.AGENDA}/${bookingId}`,
				body,
			});

			if (!response) {
				return false;
			}

			ScheduleService.reschedule(bookingId, newBooking);
			ScheduleService.emitChanges();
			logEvent(eventNames.rescheduleByInstructor, {
				bookingId: newBooking.id,
			});
			return true;
		} catch (error: any) {
			if (error.response) {
				if (error.response.status === 400 || error.response.status === 404) {
					toast.error(error.response.data?.message);
				}
			}
			return false;
		}
	};

	const upcomingSchedulers = bookings
		.filter((appointment) => new Date(appointment.startTime ?? "") > new Date())
		.sort((a, b) => {
			if (new Date(a.startTime ?? "") > new Date(b.startTime ?? "")) {
				return 1;
			}
			if (new Date(a.startTime ?? "") < new Date(b.startTime ?? "")) {
				return -1;
			}

			return 0;
		});

	const someFirstUpcomingSchedulers = upcomingSchedulers.slice(0, 2);

	const recentSchedulers = bookings.filter(
		(appointment) =>
			moment(appointment.startTime).diff(moment.now(), "day") >= -2 &&
			moment(appointment.startTime).diff(moment.now(), "second") <= 0,
	);

	return {
		loading,
		bookings,
		busyTimes,
		someFirstUpcomingSchedulers,
		recentSchedulers,
		addBusyTime,
		deleteBusyTime,
		reschedule,
	};
}
